"use client";

import { useAbility } from "@/hooks/useAbility";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

export type OrgSettingsPath =
  | "/settings/general"
  | "/settings/users"
  | "/settings/roles"
  | "/settings/reviews-playbooks";

export interface OrgSettingsAccess {
  isLoading: boolean;
  canEditOrgSettings: boolean;
  canViewUsers: boolean;
  canManageRoles: boolean;
  canViewOrgTemplates: boolean;
  canAuthorOrgTemplates: boolean;
  canDeleteOrgTemplates: boolean;
  hasAnyAccess: boolean;
  firstAccessiblePath: OrgSettingsPath | null;
}

export function useOrgSettingsAccess(organizationScopeId?: string): OrgSettingsAccess {
  const { can, isLoading } = useAbility();
  const flags = useFeatureFlags();

  const canEditOrgSettings = can("editOrgSettings", { scopeType: "ORGANIZATION", scopeId: organizationScopeId });
  const canViewUsers = can("read", { scopeType: "USER_ORG_SCOPE", scopeId: organizationScopeId });
  const canManageRoles = can("manage", { scopeType: "ROLE", scopeId: organizationScopeId });
  const orgTemplate = { scopeType: "ORG_TEMPLATE", scopeId: organizationScopeId } as const;
  const canViewOrgTemplates = canEditOrgSettings || can("read", orgTemplate);
  const canAuthorOrgTemplates = canEditOrgSettings || can("update", orgTemplate);
  const canDeleteOrgTemplates = canEditOrgSettings || can("delete", orgTemplate);

  // The Reviews & Playbooks page 404s while its flag is off, so that permission
  // only counts as settings access while the flag is on.
  const firstAccessiblePath: OrgSettingsPath | null = canEditOrgSettings
    ? "/settings/general"
    : canViewUsers
      ? "/settings/users"
      : canManageRoles
        ? "/settings/roles"
        : canViewOrgTemplates && flags["custom-reviews-playbooks"]
          ? "/settings/reviews-playbooks"
          : null;

  return {
    isLoading,
    canEditOrgSettings,
    canViewUsers,
    canManageRoles,
    canViewOrgTemplates,
    canAuthorOrgTemplates,
    canDeleteOrgTemplates,
    hasAnyAccess: firstAccessiblePath !== null,
    firstAccessiblePath,
  };
}
